import { Injectable } from '@angular/core';

export type InstallationPhotoValidationError =
	| 'unsupportedType'
	| 'originalTooLarge'
	| 'compressedTooLarge'
	| 'unableToRead';

export class InstallationPhotoError extends Error {
	constructor(readonly code: InstallationPhotoValidationError) {
		super(code);
	}
}

@Injectable({ providedIn: 'root' })
export class InstallationPhotoCompressionService {
	readonly maxOriginalSize = 15 * 1024 * 1024;
	readonly maxUploadSize = 2 * 1024 * 1024;
	private readonly allowedMimeTypes = new Set(['image/jpeg', 'image/png']);
	private readonly allowedExtensions = new Set(['jpg', 'jpeg', 'png']);

	async prepare(file: File): Promise<File> {
		const extension = file.name.split('.').pop()?.toLowerCase();
		if (
			!this.allowedMimeTypes.has(file.type.toLowerCase()) ||
			!extension ||
			!this.allowedExtensions.has(extension)
		) {
			throw new InstallationPhotoError('unsupportedType');
		}
		if (file.size > this.maxOriginalSize) {
			throw new InstallationPhotoError('originalTooLarge');
		}
		if (file.size <= this.maxUploadSize) {
			return file;
		}

		const image = await this.loadImage(file);
		let scale = Math.min(1, 1920 / Math.max(image.width, image.height));
		let quality = 0.8;

		for (let attempt = 0; attempt < 10; attempt++) {
			const blob = await this.render(image, file.type, scale, quality);
			if (blob.size <= this.maxUploadSize) {
				return new File([blob], file.name, {
					type: file.type,
					lastModified: Date.now(),
				});
			}
			if (file.type === 'image/jpeg' && quality > 0.5) {
				quality -= 0.1;
			} else {
				scale *= 0.8;
			}
		}

		throw new InstallationPhotoError('compressedTooLarge');
	}

	private loadImage(file: File): Promise<HTMLImageElement> {
		return new Promise((resolve, reject) => {
			const url = URL.createObjectURL(file);
			const image = new Image();
			image.onload = () => {
				URL.revokeObjectURL(url);
				resolve(image);
			};
			image.onerror = () => {
				URL.revokeObjectURL(url);
				reject(new InstallationPhotoError('unableToRead'));
			};
			image.src = url;
		});
	}

	private render(
		image: HTMLImageElement,
		contentType: string,
		scale: number,
		quality: number
	): Promise<Blob> {
		const canvas = document.createElement('canvas');
		canvas.width = Math.max(1, Math.round(image.width * scale));
		canvas.height = Math.max(1, Math.round(image.height * scale));
		const context = canvas.getContext('2d');
		if (!context) {
			return Promise.reject(new InstallationPhotoError('unableToRead'));
		}
		context.drawImage(image, 0, 0, canvas.width, canvas.height);

		return new Promise((resolve, reject) => {
			canvas.toBlob(
				(blob) =>
					blob
						? resolve(blob)
						: reject(new InstallationPhotoError('unableToRead')),
				contentType,
				contentType === 'image/jpeg' ? quality : undefined
			);
		});
	}
}
